import React, { useState } from 'react';
import { Badge, Card, Divider, Grid, Segmented, Tabs } from 'antd';
import { CheckCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PublicSurveyList } from './PublicSurveyList';
import { CompletedSurveysCard } from '../participant/CompletedSurveysCard';
import { CompletedSurveyItem } from '../../types/participant.types';
import { participantService } from '../../services/participant.service';

const { useBreakpoint } = Grid;

const BRAND_GREEN = '#00984A';
const PAGE_SIZE = 10;

type FinishedFilter = 'answered' | 'unanswered' | 'both';

interface ParticipantSurveyTabsProps {
  userId?: string;
  activePage: number;
  onActivePage: (page: number) => void;
  onParticipate: (surveyId: string) => void;
  getShareUrl?: (surveyId: string, publicAuditHash?: string | null) => string;
  answeredSurveys: CompletedSurveyItem[];
  answeredTotal: number;
  answeredPage: number;
  answeredPageSize: number;
  answeredTotalPages: number;
  answeredLoading: boolean;
  answeredError?: string | null;
  onAnsweredPageChange: (page: number, pageSize: number) => void;
}

export const ParticipantSurveyTabs: React.FC<ParticipantSurveyTabsProps> = ({
  userId,
  activePage,
  onActivePage,
  onParticipate,
  getShareUrl,
  answeredSurveys,
  answeredTotal,
  answeredPage,
  answeredPageSize,
  answeredTotalPages,
  answeredLoading,
  answeredError,
  onAnsweredPageChange,
}) => {
  const [unansweredPage, setUnansweredPage] = useState(1);
  const [finishedFilter, setFinishedFilter] = useState<FinishedFilter>('answered');
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data: activeData, isLoading: activeLoading, isFetching: activeFetching } = useQuery({
    queryKey: ['participant-active', userId, activePage],
    queryFn: () => participantService.getActiveSurveys(activePage, PAGE_SIZE),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: unansweredData, isLoading: unansweredLoading, isFetching: unansweredFetching } = useQuery({
    queryKey: ['participant-unanswered', userId, unansweredPage],
    queryFn: () => participantService.getUnansweredCompleted(unansweredPage, PAGE_SIZE),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const activeCount = activeData?.totalItems;
  const finishedCount = answeredTotal + (unansweredData?.totalItems ?? 0);

  const tabLabel = (
    icon: React.ReactNode,
    label: string,
    count: number | undefined,
    badgeColor: string,
  ) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {icon}
      {label}
      {count !== undefined && count > 0 && (
        <Badge
          count={count}
          overflowCount={99}
          style={{ backgroundColor: badgeColor, boxShadow: 'none' }}
        />
      )}
    </span>
  );

  const TAB_MIN_HEIGHT = 320;

  const filterOptions = [
    { label: 'Respondidas', value: 'answered' },
    { label: 'Não Respondidas', value: 'unanswered' },
  ];

  const finishedContent = (
    <div style={{ minHeight: TAB_MIN_HEIGHT }}>
      <div style={{ marginBottom: 20 }}>
        <Segmented
          options={filterOptions}
          value={finishedFilter}
          onChange={(v) => setFinishedFilter(v as FinishedFilter)}
          block={isMobile}
        />
      </div>

      {(finishedFilter === 'answered' || finishedFilter === 'both') && (
        <>
          {finishedFilter === 'both' && (
            <Divider orientation="left" style={{ marginTop: 0 }}>Respondidas</Divider>
          )}
          <CompletedSurveysCard
            surveys={answeredSurveys}
            total={answeredTotal}
            page={answeredPage}
            pageSize={answeredPageSize}
            totalPages={answeredTotalPages}
            isLoading={answeredLoading}
            error={answeredError}
            onPageChange={onAnsweredPageChange}
            noCard
          />
        </>
      )}

      {finishedFilter === 'both' && <Divider orientation="left">Não Respondidas</Divider>}

      {(finishedFilter === 'unanswered' || finishedFilter === 'both') && (
        <PublicSurveyList
          title="Pesquisas Não Respondidas"
          showTitle={false}
          data={unansweredData}
          loading={unansweredLoading}
          fetching={unansweredFetching}
          page={unansweredPage}
          onPageChange={setUnansweredPage}
        />
      )}
    </div>
  );

  return (
    <Card
      style={{ borderRadius: 12, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', border: 'none', overflow: 'hidden' }}
      styles={{ body: { padding: isMobile ? '0 12px 20px' : '0 24px 28px' } }}
    >
      <Tabs
        defaultActiveKey="active"
        size={isMobile ? 'middle' : 'large'}
        items={[
          {
            key: 'active',
            label: tabLabel(
              <FileTextOutlined />,
              'Pesquisas Ativas',
              activeCount,
              BRAND_GREEN,
            ),
            children: (
              <div style={{ minHeight: TAB_MIN_HEIGHT }}>
                <PublicSurveyList
                  title="Pesquisas Ativas"
                  showTitle={false}
                  data={activeData}
                  loading={activeLoading}
                  fetching={activeFetching}
                  page={activePage}
                  onPageChange={onActivePage}
                  onParticipate={onParticipate}
                  getShareUrl={getShareUrl}
                />
              </div>
            ),
          },
          {
            key: 'finished',
            label: tabLabel(
              <CheckCircleOutlined />,
              isMobile ? 'Finalizadas' : 'Pesquisas Finalizadas',
              finishedCount,
              '#52c41a',
            ),
            children: finishedContent,
          },
        ]}
      />

    </Card>
  );
};
